import api from '../application/api';

const STORAGE_KEY = 'favoriteApplicationIds';

const sameId = (a, b) => String(a) === String(b);

let hydrateToken = 0;

const readStoredIds = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export default {
  namespaced: true,
  state: {
    ids: [],
    applications: [],
  },
  mutations: {
    setFavoriteIds(state, ids) {
      // localStorage is user-controlled — keep only non-empty primitive ids, drop duplicates
      const valid = ids.filter(
        id => (typeof id === 'string' || typeof id === 'number') && String(id).length > 0
      );
      const normalized = valid.filter(
        (id, index) => valid.findIndex(other => sameId(other, id)) === index
      );

      state.ids = normalized;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
      } catch (error) {
        // storage unavailable or full — favorites only last for this session
      }
    },
    setFavoriteApplications(state, applications) {
      state.applications = applications;
    },
  },
  getters: {
    getFavoriteApplications: state => state.applications,
    isFavorite: state => id => state.ids.some(favoriteId => sameId(favoriteId, id)),
  },
  actions: {
    init({commit}) {
      commit('setFavoriteIds', readStoredIds());
    },
    toggle({state, commit, getters}, id) {
      if (getters.isFavorite(id)) {
        commit('setFavoriteIds', state.ids.filter(favoriteId => !sameId(favoriteId, id)));
        commit(
          'setFavoriteApplications',
          state.applications.filter(application => !sameId(application.id, id))
        );
        return Promise.resolve();
      }

      commit('setFavoriteIds', [...state.ids, id]);

      // fetch only the new favorite instead of re-hydrating all of them
      return api.find(id)
        .then(response => {
          const application = response.data.data;

          if (state.ids.some(favoriteId => sameId(favoriteId, id)) &&
              !state.applications.some(existing => sameId(existing.id, application.id))) {
            commit('setFavoriteApplications', [...state.applications, application]);
          }
        })
        .catch(() => {
          // not hydrated now — the id is retried (or pruned on 404) by the next hydrate
        });
    },
    hydrate({state, commit}) {
      const token = ++hydrateToken;
      const ids = [...state.ids];

      if (ids.length === 0) {
        commit('setFavoriteApplications', []);
        return Promise.resolve();
      }

      return Promise.all(
        ids.map(id => api.find(id)
          .then(response => ({id, application: response.data.data}))
          .catch(error => ({
            id,
            application: null,
            deleted: Boolean(error.response && error.response.status === 404),
          })))
      ).then(results => {
        // ids may have changed while fetching — only the latest hydrate may commit
        if (token !== hydrateToken) {
          return;
        }

        // rebuild the list from the CURRENT ids: merge what was fetched with
        // applications toggle added while these requests were in flight
        const fetched = results.map(result => result.application).filter(Boolean);
        const available = [
          ...fetched,
          ...state.applications.filter(
            existing => !fetched.some(application => sameId(application.id, existing.id))
          ),
        ];
        commit('setFavoriteApplications', state.ids
          .map(favoriteId => available.find(application => sameId(application.id, favoriteId)))
          .filter(Boolean));

        // only prune favorites whose application no longer exists;
        // transient errors keep the id and retry on the next hydrate
        const deletedIds = results.filter(result => result.deleted).map(result => result.id);
        if (deletedIds.length) {
          commit('setFavoriteIds', state.ids.filter(
            favoriteId => !deletedIds.some(deletedId => sameId(deletedId, favoriteId))
          ));
        }
      });
    },
  },
};
